import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, UserItem } from "../../lib/api";
import { isSuper, useAuth } from "../../lib/auth";
import { Plus, Trash2, KeyRound, X } from "lucide-react";

export function UsersPage() {
  const { user: actor } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [tempPwShown, setTempPwShown] = useState<{ email: string; pw: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<UserItem[]>("/api/v1/users"),
  });

  const create = useMutation({
    mutationFn: (payload: any) => api.post<UserItem>("/api/v1/users", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setAdding(false);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const reset = useMutation({
    mutationFn: (id: string) => api.post<{ new_temporary_password: string }>(`/api/v1/users/${id}/reset-password`),
    onSuccess: (data, id) => {
      const u = (qc.getQueryData(["users"]) as UserItem[] | undefined)?.find(x => x.user_id === id);
      setTempPwShown({ email: u?.email || "?", pw: data.new_temporary_password });
    },
  });

  return (
    <div style={{ padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, color: "#1a365d", margin: 0 }}>
          Користувачі {data && <span style={{ color: "#718096", fontSize: 14 }}>({data.length})</span>}
        </h1>
        <button onClick={() => setAdding(true)} style={btnPrimary}>
          <Plus size={14} /> Новий
        </button>
      </div>

      {isLoading && <div>Завантаження…</div>}

      {data && (
        <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 13.5, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
              <th style={th}>Email</th><th style={th}>Ім'я</th><th style={th}>Роль</th>
              <th style={th}>Активний</th><th style={th}>Створено</th><th style={th}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.user_id} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={td}>{u.email}</td>
                <td style={td}>{u.full_name || "—"}</td>
                <td style={td}><span style={roleBadge(u.role)}>{u.role}</span></td>
                <td style={td}>{u.is_active ? "✓" : "⏸"}</td>
                <td style={td}>{new Date(u.created_at).toLocaleDateString("uk-UA")}</td>
                <td style={td}>
                  <button onClick={() => reset.mutate(u.user_id)} style={btnSecondary} title="Reset password">
                    <KeyRound size={12} />
                  </button>
                  {isSuper(actor?.role) && u.user_id !== actor?.user_id && (
                    <button onClick={() => confirm(`Видалити ${u.email}?`) && del.mutate(u.user_id)}
                            style={{ ...btnSecondary, marginLeft: 4, color: "#e53e3e", borderColor: "#fed7d7" }} title="Delete">
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <Modal onClose={() => setAdding(false)}>
          <CreateUserForm
            isSuper={isSuper(actor?.role)}
            onSubmit={(payload) => create.mutate(payload)}
            error={create.error ? (create.error as any).detail : null}
          />
        </Modal>
      )}

      {tempPwShown && (
        <Modal onClose={() => setTempPwShown(null)}>
          <h3 style={{ marginTop: 0 }}>Новий тимчасовий пароль</h3>
          <p>Користувач: <strong>{tempPwShown.email}</strong></p>
          <pre style={{ background: "#f7fafc", padding: 12, borderRadius: 4, fontSize: 14 }}>
            {tempPwShown.pw}
          </pre>
          <p style={{ fontSize: 12, color: "#718096" }}>
            Передайте цей пароль користувачу. Він має змінити його при першому вході.
          </p>
        </Modal>
      )}
    </div>
  );
}

function CreateUserForm({ isSuper, onSubmit, error }: { isSuper: boolean; onSubmit: (p: any) => void; error: string | null }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [role, setRole] = useState("viewer");

  function gen() {
    const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    setPw(Array.from({ length: 14 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join(""));
  }

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ email, full_name: name, password: pw, role }); }}>
      <h3 style={{ marginTop: 0 }}>Новий користувач</h3>
      <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required style={inp} /></Field>
      <Field label="Ім'я"><input value={name} onChange={(e) => setName(e.target.value)} style={inp} /></Field>
      <Field label="Роль">
        <select value={role} onChange={(e) => setRole(e.target.value)} style={inp}>
          <option value="viewer">viewer</option>
          <option value="analyst">analyst</option>
          <option value="donor">donor</option>
          <option value="admin">admin</option>
          {isSuper && <option value="super_admin">super_admin</option>}
        </select>
      </Field>
      <Field label="Пароль">
        <div style={{ display: "flex", gap: 4 }}>
          <input value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} style={inp} />
          <button type="button" onClick={gen} style={btnSecondary}>Згенерувати</button>
        </div>
      </Field>
      {error && <div style={{ color: "#c53030", fontSize: 13, margin: "8px 0" }}>{error}</div>}
      <button type="submit" style={{ ...btnPrimary, marginTop: 12 }}>Створити</button>
    </form>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} className="modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="modal-card" style={{ width: 480, maxWidth: "92vw", position: "relative" }}>
        <button onClick={onClose} aria-label="close"
                style={{ position: "absolute", top: 12, right: 12, width: 28, height: 28, borderRadius: "50%", background: "transparent", color: "var(--c-text-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--c-surface-hover)"; e.currentTarget.style.color = "var(--c-danger)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--c-text-muted)"; }}>
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 12, color: "#4a5568", marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

function roleBadge(role: string): React.CSSProperties {
  const colors: Record<string, string> = {
    super_admin: "#9b2c2c", admin: "#2c5282", analyst: "#2f855a", viewer: "#718096", donor: "#805ad5",
  };
  return {
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: colors[role] || "#4a5568",
    background: (colors[role] || "#718096") + "15",
  };
}

const inp: React.CSSProperties = { padding: "7px 10px", fontSize: 13, border: "1px solid #cbd5e0", borderRadius: 4, outline: "none", width: "100%", boxSizing: "border-box" };
const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 12px" };
const btnPrimary: React.CSSProperties = { padding: "7px 14px", background: "#2c5282", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 };
const btnSecondary: React.CSSProperties = { padding: "5px 10px", background: "white", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: 4, cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 };
